import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Check, Send } from "lucide-react";
import { Button, Field, Input, Textarea } from "@/design/components";
import { useSubmitProjectCreationRequest } from "@/domain/useProjectCreation";

// ZEUS-133 — "Request Project Creation" / "Propose a Project" modal for
// Contractors/Subcontractors, who can't create projects directly (ZEUS-127
// restricts that to stratus_admin). Deliberately labeled as a request
// throughout — never "Create" — so it can't be mistaken for the direct
// creation flow (ZEUS-132, stratus_admin only).
//
// Field scope trimmed to what POST /api/project-creation-request actually
// accepts (see .claude/implementation-plan.md on this branch) — the
// originating spec's "urgency/priority", "notes", "attachments" have no
// backing column on the projectcreationrequest table and aren't offered
// here.

export function RequestProjectCreationModal({
  open,
  onOpenChange,
  programmeId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Raw id (workspace/programme) — useSubmitProjectCreationRequest converts as needed. */
  programmeId?: string;
}) {
  const { submitProjectCreationRequest, isPending, error } = useSubmitProjectCreationRequest();

  const [proposedName, setProposedName] = useState("");
  const [proposedDescription, setProposedDescription] = useState("");
  const [justification, setJustification] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const canSubmit =
    proposedName.trim().length > 0 &&
    proposedDescription.trim().length > 0 &&
    justification.trim().length > 0;

  function reset() {
    setProposedName("");
    setProposedDescription("");
    setJustification("");
    setSubmitted(false);
  }

  async function onSubmit() {
    if (!canSubmit) return;
    const result = await submitProjectCreationRequest({
      proposedName: proposedName.trim(),
      proposedDescription: proposedDescription.trim(),
      justification: justification.trim(),
      programmeId,
    });
    if (result) setSubmitted(true);
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {submitted ? "Request submitted" : "Propose a project"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {submitted ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Your request for <strong>{proposedName}</strong> has been sent to Stratus for
                  review. You'll be notified once it's accepted or rejected.
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                This is a request, not a direct creation — Stratus reviews it and either accepts
                (creating the project) or rejects it with a reason.
              </Dialog.Description>

              <Field label="Proposed project name">
                <Input
                  value={proposedName}
                  onChange={(e) => setProposedName(e.target.value)}
                  placeholder="e.g. Fire Station #25 Jinja"
                />
              </Field>

              <Field label="Proposed description">
                <Textarea
                  value={proposedDescription}
                  onChange={(e) => setProposedDescription(e.target.value)}
                  rows={2}
                  placeholder="What is this project and what will it deliver?"
                />
              </Field>

              <Field label="Justification">
                <Textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={3}
                  placeholder="Why is this project necessary?"
                />
              </Field>

              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {!submitted && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canSubmit || isPending} onClick={onSubmit}>
                <Send className="h-4 w-4" /> {isPending ? "Submitting…" : "Submit request"}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
